(() => {
  if (customElements.get('custom-size-advisor')) return;

  class CustomSizeAdvisor extends HTMLElement {
    connectedCallback() {
      this.dialog =
        this.querySelector('.custom-size-advisor__dialog') ||
        this.querySelector('dialog');

      this.panel = this.querySelector('.custom-size-advisor__panel');
      this.configElement = this.querySelector('[data-custom-size-advisor-config]');
      this.form = this.querySelector('[data-custom-size-advisor-form]');
      this.result = this.querySelector('[data-custom-size-advisor-result]');
      this.resultSize = this.querySelector('[data-custom-size-advisor-result-size]');
      this.resultText = this.querySelector('[data-custom-size-advisor-result-text]');
      this.resultIconAvailable = this.querySelector('[data-custom-size-advisor-result-icon-available]');
      this.selectButton = this.querySelector('[data-custom-size-advisor-select]');
      this.selectAlternativeButton = this.querySelector('[data-custom-size-advisor-select-alternative]');
      this.views = this.querySelectorAll('[data-custom-size-advisor-view]');
      this.steps = this.querySelector('[data-custom-size-advisor-steps]');
      this.backButton = this.querySelector('[data-custom-size-advisor-back]');
      this.errorElement = this.querySelector('[data-custom-size-advisor-error]');

      this.loadingTimeout = null;
      this.drawerFallbackTimer = null;
      this.config = {};
      this.rows = [];
      this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex || 0);
      this.currentRecommendation = null;
      this.currentAlternative = null;
      this.isClosing = false;
      this.isOpening = false;

      if (!this.dialog || !this.panel) return;

      this.parseConfig();
      this.bindEvents();
      this.setTab('advisor');
      this.prepareClosedDrawerState();
    }

    parseConfig() {
      if (!this.configElement) return;

      try {
        this.config = JSON.parse(this.configElement.textContent || '{}');
      } catch (error) {
        this.config = {};
      }

      this.rows = Array.isArray(this.config.rows)
        ? [...this.config.rows].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
        : [];

      this.sizeOptionIndex = Number(this.config.sizeOptionIndex ?? this.sizeOptionIndex ?? 0);
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.querySelectorAll('input, button, select, textarea').forEach((element) => {
        ['input', 'change', 'submit'].forEach((eventName) => {
          element.addEventListener(eventName, (event) => {
            event.stopPropagation();
          });
        });
      });

      this.querySelectorAll('[data-custom-size-advisor-close]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        });
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.isDrawerOpen()) return;

        event.preventDefault();
        this.close();
      });

      this.querySelectorAll('[data-custom-size-advisor-tab]').forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.setTab(tab.getAttribute('data-custom-size-advisor-tab'));
        });
      });

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.recommend();
        });
      }

      if (this.backButton) {
        this.backButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.showStep('input');
        });
      }

      if (this.selectButton) {
        this.selectButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!this.currentRecommendation) return;
          this.selectSize(this.currentRecommendation.sizeLabel);
        });
      }

      if (this.selectAlternativeButton) {
        this.selectAlternativeButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (!this.currentAlternative) return;
          this.selectSize(this.currentAlternative.sizeLabel);
        });
      }
    }

    isNativeDialog() {
      return typeof this.dialog.showModal === 'function' && typeof this.dialog.close === 'function';
    }

    isDrawerOpen() {
      if (!this.dialog) return false;

      if (this.isNativeDialog()) {
        return this.dialog.open;
      }

      return this.dialog.hidden === false;
    }

    prepareClosedDrawerState() {
      if (!this.panel) return;

      this.panel.style.setProperty('animation', 'none', 'important');
      this.panel.style.setProperty('transition', 'none', 'important');
      this.panel.style.setProperty('transform', 'translate3d(100%, 0, 0)', 'important');
      this.panel.style.setProperty('opacity', '0', 'important');
    }

    open() {
      if (!this.dialog || !this.panel || this.isDrawerOpen() || this.isOpening) return;

      this.isOpening = true;
      this.isClosing = false;
      this.clearDrawerFallbackTimer();

      const drawerTransition =
        'transform 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease-out';

      this.dialog.classList.remove('is-open', 'is-opening', 'is-closing');

      this.panel.getAnimations().forEach((animation) => animation.cancel());

      this.panel.style.setProperty('animation', 'none', 'important');
      this.panel.style.setProperty('transition', 'none', 'important');
      this.panel.style.setProperty('opacity', '0', 'important');

      this.lockPageScroll();

      if (this.isNativeDialog()) {
        this.dialog.showModal();
      } else {
        this.dialog.hidden = false;
      }

      document.documentElement.classList.add('custom-size-advisor-is-open');

      this.panel.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');

      const panelRect = this.panel.getBoundingClientRect();
      const closedX = Math.ceil(window.innerWidth - panelRect.left);

      this.panel.style.setProperty('transform', `translate3d(${closedX}px, 0, 0)`, 'important');
      this.panel.getBoundingClientRect();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.dialog.classList.add('is-open');

          this.panel.style.setProperty('transition', drawerTransition, 'important');
          this.panel.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
          this.panel.style.setProperty('opacity', '1', 'important');
        });
      });

      const finishOpen = (event) => {
        if (event && (event.target !== this.panel || event.propertyName !== 'transform')) return;

        this.panel.removeEventListener('transitionend', finishOpen);
        this.clearDrawerFallbackTimer();

        this.panel.style.setProperty('transition', 'none', 'important');
        this.panel.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
        this.panel.style.setProperty('opacity', '1', 'important');

        this.isOpening = false;

        if (typeof this.panel.focus === 'function') {
          this.panel.focus({ preventScroll: true });
        }
      };

      this.panel.addEventListener('transitionend', finishOpen);
      this.drawerFallbackTimer = window.setTimeout(finishOpen, 460);
    }

    close() {
      if (!this.dialog || !this.panel || !this.isDrawerOpen() || this.isClosing) return;

      this.isClosing = true;
      this.isOpening = false;
      this.clearDrawerFallbackTimer();

      const drawerTransition =
        'transform 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out';

      this.dialog.classList.remove('is-opening', 'is-closing');

      this.panel.getAnimations().forEach((animation) => animation.cancel());

      this.panel.style.setProperty('animation', 'none', 'important');
      this.panel.style.setProperty('transition', 'none', 'important');
      this.panel.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
      this.panel.style.setProperty('opacity', '1', 'important');

      const panelRect = this.panel.getBoundingClientRect();
      const closedX = Math.ceil(window.innerWidth - panelRect.left);

      this.panel.getBoundingClientRect();

      requestAnimationFrame(() => {
        this.dialog.classList.remove('is-open');

        this.panel.style.setProperty('transition', drawerTransition, 'important');
        this.panel.style.setProperty('transform', `translate3d(${closedX}px, 0, 0)`, 'important');
        this.panel.style.setProperty('opacity', '0', 'important');
      });

      const finishClose = (event) => {
        if (event && (event.target !== this.panel || event.propertyName !== 'transform')) return;

        this.panel.removeEventListener('transitionend', finishClose);
        this.clearDrawerFallbackTimer();

        this.panel.style.setProperty('transition', 'none', 'important');
        this.panel.style.setProperty('transform', `translate3d(${closedX}px, 0, 0)`, 'important');
        this.panel.style.setProperty('opacity', '0', 'important');

        if (this.isNativeDialog() && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.hidden = true;
        }

        document.documentElement.classList.remove('custom-size-advisor-is-open');
        this.unlockPageScroll();

        this.isClosing = false;
      };

      this.panel.addEventListener('transitionend', finishClose);
      this.drawerFallbackTimer = window.setTimeout(finishClose, 460);
    }

    clearDrawerFallbackTimer() {
      if (!this.drawerFallbackTimer) return;

      window.clearTimeout(this.drawerFallbackTimer);
      this.drawerFallbackTimer = null;
    }

    lockPageScroll() {
      document.documentElement.classList.add('custom-size-advisor-is-open');
    }

    unlockPageScroll() {
      document.documentElement.classList.remove('custom-size-advisor-is-open');
    }

    getText(key, variables = {}) {
      const texts = this.config.texts || {};
      const template = texts[key] || '';

      return this.formatText(template, variables);
    }

    formatText(template, variables = {}) {
      return String(template || '').replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
        return variables[key] ?? '';
      });
    }

    getFitSpreadTextKey(level, basisCount, smallerCount) {
      const basisPart = basisCount === 1 ? 'One' : 'Other';
      const smallerPart = smallerCount === 1 ? 'One' : 'Other';

      if (level === 'high') {
        return `fitSpreadHigh${basisPart}${smallerPart}`;
      }

      return `fitSpreadMedium${basisPart}${smallerPart}`;
    }

    formatHumanList(items) {
      const cleanItems = items.filter(Boolean);

      if (cleanItems.length === 0) return '';
      if (cleanItems.length === 1) return cleanItems[0];

      try {
        const locale = document.documentElement.lang || undefined;
        return new Intl.ListFormat(locale, {
          style: 'long',
          type: 'conjunction',
        }).format(cleanItems);
      } catch (error) {
        return cleanItems.join(', ');
      }
    }

    setTab(tabName) {
      if (!tabName) return;

      this.setAttribute('data-active-panel', tabName);

      const tabs = this.querySelector('[data-custom-size-advisor-tabs]');
      if (tabs) {
        tabs.setAttribute('data-active-tab', tabName);
      }

      this.querySelectorAll('[data-custom-size-advisor-tab]').forEach((tab) => {
        const isActive = tab.getAttribute('data-custom-size-advisor-tab') === tabName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.querySelectorAll('[data-custom-size-advisor-panel]').forEach((panel) => {
        const isActive = panel.getAttribute('data-custom-size-advisor-panel') === tabName;

        if (isActive) {
          panel.hidden = false;

          requestAnimationFrame(() => {
            panel.classList.add('is-active');
          });
        } else {
          panel.classList.remove('is-active');
          panel.hidden = true;
        }
      });

      if (tabName === 'advisor') {
        this.showStep('input');
      }
    }

    showStep(stepName) {
      if (!stepName) return;

      this.views.forEach((view) => {
        const isActive = view.getAttribute('data-custom-size-advisor-view') === stepName;

        if (isActive) {
          view.hidden = false;

          requestAnimationFrame(() => {
            view.classList.add('is-active');
          });
        } else {
          view.classList.remove('is-active');
          view.hidden = true;
        }
      });

      if (this.steps) {
        this.steps.setAttribute('data-step', stepName);
      }

      this.querySelectorAll('[data-custom-size-advisor-step-item]').forEach((step) => {
        const item = step.getAttribute('data-custom-size-advisor-step-item');

        const isInputActive = item === 'input' && stepName === 'input';
        const isInputDone = item === 'input' && (stepName === 'loading' || stepName === 'result');
        const isResultActive = item === 'result' && (stepName === 'loading' || stepName === 'result');

        step.classList.toggle('is-active', isInputActive);
        step.classList.toggle('is-done', isInputDone);
        step.classList.toggle('is-current', isResultActive);
      });
    }

    showInputError(message) {
      if (!this.errorElement) return;

      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearInputError() {
      if (!this.errorElement) return;

      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    recommend() {
      const recommendation = this.findRecommendation();

      if (!recommendation) {
        this.showInputError(this.getText('inputError'));
        return;
      }

      this.clearInputError();

      if (this.loadingTimeout) {
        window.clearTimeout(this.loadingTimeout);
      }

      this.currentRecommendation = recommendation.row;
      this.currentAlternative = null;

      this.showStep('loading');

      this.loadingTimeout = window.setTimeout(() => {
        this.showRecommendation(recommendation);
        this.showStep('result');
      }, 650);
    }

    findRecommendation() {
      const inputType = this.config.inputType;

      if (inputType === 'body_measurements') {
        return this.findBodyMeasurementRecommendation();
      }

      const input = this.querySelector('[data-custom-size-advisor-input="measurement"]');
      const value = this.parseNumber(input?.value);

      if (!value) return null;

      if (inputType === 'child_height_cm') {
        return this.findByRange(value, 'bodyMinCm', 'bodyMaxCm');
      }

      if (inputType === 'foot_length_cm') {
        return this.findByRange(value, 'footMinCm', 'footMaxCm');
      }

      if (inputType === 'shoe_size') {
        return this.findByRange(value, 'shoeMin', 'shoeMax');
      }

      return null;
    }

    normalizeSizeLabel(value) {
      return String(value ?? '').trim();
    }

    getProductSizeLabels() {
      const variants = this.config.product?.variants || [];
      const labels = [];

      variants.forEach((variant) => {
        const sizeLabel = this.normalizeSizeLabel(variant.options?.[this.sizeOptionIndex]);

        if (sizeLabel && !labels.includes(sizeLabel)) {
          labels.push(sizeLabel);
        }
      });

      return labels;
    }

    getProductRows() {
      const productSizeLabels = this.getProductSizeLabels();

      if (!productSizeLabels.length) {
        return this.rows;
      }

      return this.rows.filter((row) => {
        return productSizeLabels.includes(this.normalizeSizeLabel(row.sizeLabel));
      });
    }

    isOutOfProductRangeStatus(status) {
      return status === 'too_small_for_product' || status === 'too_large_for_product';
    }

    findByRange(value, minKey, maxKey) {
      const productRows = this.getProductRows();

      const rowsWithRange = productRows.filter((row) => {
        return row[minKey] !== null && row[minKey] !== undefined && row[maxKey] !== null && row[maxKey] !== undefined;
      });

      if (!rowsWithRange.length) return null;

      const matchingRows = rowsWithRange.filter((row) => {
        return this.valueInRange(value, row[minKey], row[maxKey]);
      });

      if (matchingRows.length) {
        return {
          row: matchingRows[matchingRows.length - 1],
          status: matchingRows.length > 1 ? 'between' : 'match',
        };
      }

      const first = rowsWithRange[0];
      const last = rowsWithRange[rowsWithRange.length - 1];

      if (value < Number(first[minKey])) {
        return {
          row: first,
          status: 'too_small_for_product',
          outOfProductRange: true,
        };
      }

      if (value > Number(last[maxKey])) {
        return {
          row: last,
          status: 'too_large_for_product',
          outOfProductRange: true,
        };
      }

      const nextLarger = rowsWithRange.find((row) => value < Number(row[minKey]));

      return {
        row: nextLarger || last,
        status: 'between',
      };
    }

    getRowPosition(row, rows = this.getProductRows()) {
      if (!row) return -1;

      return rows.findIndex((candidate) => {
        return this.normalizeSizeLabel(candidate.sizeLabel) === this.normalizeSizeLabel(row.sizeLabel);
      });
    }

    findMeasurementResult(value, rows, definition) {
      const rowsWithRange = rows.filter((row) => {
        return (
          row[definition.minKey] !== null &&
          row[definition.minKey] !== undefined &&
          row[definition.maxKey] !== null &&
          row[definition.maxKey] !== undefined
        );
      });

      if (!rowsWithRange.length) return null;

      const matchingRows = rowsWithRange.filter((row) => {
        return this.valueInRange(value, row[definition.minKey], row[definition.maxKey]);
      });

      let row = null;
      let status = 'match';

      if (matchingRows.length) {
        row = matchingRows[matchingRows.length - 1];
        status = matchingRows.length > 1 ? 'between' : 'match';
      } else {
        const first = rowsWithRange[0];
        const last = rowsWithRange[rowsWithRange.length - 1];

        if (value < Number(first[definition.minKey])) {
          row = first;
          status = 'below';
        } else if (value > Number(last[definition.maxKey])) {
          row = last;
          status = 'above';
        } else {
          row = rowsWithRange.find((candidate) => value < Number(candidate[definition.minKey])) || last;
          status = 'between';
        }
      }

      return {
        ...definition,
        value,
        row,
        status,
        index: this.getRowPosition(row, rows),
      };
    }

    findBodyMeasurementRecommendation() {
      const productRows = this.getProductRows();

      if (!productRows.length) return null;

      const measurementLabels = this.config.measurementLabels || {};

      const measurementDefinitions = [
        {
          input: 'chest',
          label: measurementLabels.chest || '',
          minKey: 'chestMinCm',
          maxKey: 'chestMaxCm',
        },
        {
          input: 'waist',
          label: measurementLabels.waist || '',
          minKey: 'waistMinCm',
          maxKey: 'waistMaxCm',
        },
        {
          input: 'hip',
          label: measurementLabels.hip || '',
          minKey: 'hipMinCm',
          maxKey: 'hipMaxCm',
        },
      ];

      const enteredMeasurements = measurementDefinitions
        .map((definition) => {
          const value = this.parseNumber(
            this.querySelector(`[data-custom-size-advisor-input="${definition.input}"]`)?.value
          );

          if (!value) return null;

          return {
            ...definition,
            value,
          };
        })
        .filter(Boolean);

      if (!enteredMeasurements.length) return null;

      const measurementResults = enteredMeasurements
        .map((definition) => this.findMeasurementResult(definition.value, productRows, definition))
        .filter(Boolean);

      if (!measurementResults.length) return null;

      const hasTooLargeMeasurement = measurementResults.some((result) => result.status === 'above');
      const allMeasurementsTooSmall = measurementResults.every((result) => result.status === 'below');

      if (hasTooLargeMeasurement) {
        const lastRow = productRows[productRows.length - 1];

        return {
          row: lastRow,
          status: 'too_large_for_product',
          outOfProductRange: true,
          measurementResults,
          fitMessage: '',
        };
      }

      if (allMeasurementsTooSmall) {
        const firstRow = productRows[0];

        return {
          row: firstRow,
          status: 'too_small_for_product',
          outOfProductRange: true,
          measurementResults,
          fitMessage: '',
        };
      }

      const selectedResult = measurementResults.reduce((currentBest, result) => {
        if (!currentBest) return result;
        return result.index > currentBest.index ? result : currentBest;
      }, null);

      if (!selectedResult || !selectedResult.row) return null;

      const indices = measurementResults
        .map((result) => result.index)
        .filter((index) => index >= 0);

      const spread = indices.length ? Math.max(...indices) - Math.min(...indices) : 0;

      return {
        row: selectedResult.row,
        status: spread > 0 ? 'between' : 'match',
        measurementResults,
        fitSpread: spread,
        fitMessage: this.buildBodyMeasurementFitMessage(measurementResults, selectedResult.row),
      };
    }

    buildBodyMeasurementFitMessage(measurementResults, selectedRow) {
      if (!measurementResults || !measurementResults.length || !selectedRow) return '';

      const validResults = measurementResults.filter((result) => result.index >= 0);
      if (!validResults.length) return '';

      const indices = validResults.map((result) => result.index);
      const minIndex = Math.min(...indices);
      const maxIndex = Math.max(...indices);
      const spread = maxIndex - minIndex;

      if (spread <= 1) return '';

      const selectedSize = selectedRow.sizeLabel;

      const basisLabels = validResults
        .filter((result) => result.index === maxIndex)
        .map((result) => result.label)
        .filter(Boolean);

      const clearlySmallerLabels = validResults
        .filter((result) => result.index <= maxIndex - 2)
        .map((result) => result.label)
        .filter(Boolean);

      const basisText = this.formatHumanList(basisLabels);
      const smallerText = this.formatHumanList(clearlySmallerLabels);

      if (spread >= 3) {
        if (smallerText) {
          const key = this.getFitSpreadTextKey('high', basisLabels.length, clearlySmallerLabels.length);

          return this.getText(key, {
            size: selectedSize,
            basis: basisText,
            smaller: smallerText,
          });
        }

        return this.getText('fitSpreadHighFallback', {
          size: selectedSize,
        });
      }

      if (smallerText) {
        const key = this.getFitSpreadTextKey('medium', basisLabels.length, clearlySmallerLabels.length);

        return this.getText(key, {
          basis: basisText,
          smaller: smallerText,
        });
      }

      return this.getText('fitSpreadBetween', {
        size: selectedSize,
      });
    }

    valueInRange(value, min, max) {
      if (min === null || max === null || min === undefined || max === undefined) return false;

      return Number(value) >= Number(min) && Number(value) <= Number(max);
    }

    showRecommendation(recommendation) {
      const row = recommendation.row;
      const sizeLabel = row.sizeLabel;
      const variant = this.findVariantForSize(sizeLabel);
      const isOutOfProductRange = this.isOutOfProductRangeStatus(recommendation.status);
      const hasAvailableRecommendedVariant = Boolean(variant && variant.available && !isOutOfProductRange);

      if (this.resultIconAvailable) {
        this.resultIconAvailable.hidden = !hasAvailableRecommendedVariant;
      }

      if (!this.result) return;

      this.result.hidden = false;

      requestAnimationFrame(() => {
        this.result.classList.add('is-visible');
      });

      if (this.resultSize) {
        if (recommendation.status === 'too_small_for_product') {
          this.resultSize.innerHTML = `${this.getText('smallestSizePrefix')} <strong>${sizeLabel}</strong>`;
        } else if (recommendation.status === 'too_large_for_product') {
          this.resultSize.innerHTML = `${this.getText('largestSizePrefix')} <strong>${sizeLabel}</strong>`;
        } else {
          this.resultSize.innerHTML = `${this.getText('recommendedPrefix')} <strong>${sizeLabel}</strong>`;
        }
      }

      const messageParts = [];

      const isBodyMeasurementAdvisor = this.config.inputType === 'body_measurements';

      if (recommendation.status === 'too_small_for_product') {
        messageParts.push(
          this.getText(
            isBodyMeasurementAdvisor ? 'tooSmallForProductBody' : 'tooSmallForProductChild',
            { size: sizeLabel }
          )
        );
      } else if (recommendation.status === 'too_large_for_product') {
        messageParts.push(
          this.getText(
            isBodyMeasurementAdvisor ? 'tooLargeForProductBody' : 'tooLargeForProductChild',
            { size: sizeLabel }
          )
        );
      }

      if (!isOutOfProductRange) {
        if (recommendation.fitMessage) {
          messageParts.push(recommendation.fitMessage);
        }

        if (!variant) {
          messageParts.push(this.getText('noVariant'));
        } else if (!variant.available) {
          messageParts.push(this.getText('soldOut'));
        }
      }

      if (this.resultText) {
        this.resultText.textContent = messageParts.filter(Boolean).join(' ');
      }

      if (variant && variant.available && !isOutOfProductRange && this.selectButton) {
        const selectSizeText = this.getText('selectSize');

        this.selectButton.hidden = false;
        this.selectButton.disabled = false;
        this.selectButton.textContent = selectSizeText ? `${selectSizeText}: ${sizeLabel}` : sizeLabel;
      } else if (this.selectButton) {
        this.selectButton.hidden = true;
        this.selectButton.disabled = true;
      }

      this.currentAlternative = null;

      if (this.selectAlternativeButton) {
        this.selectAlternativeButton.hidden = true;
        this.selectAlternativeButton.disabled = true;
      }
    }

    showMessage(message = '') {
      this.showStep('result');

      if (!this.result) return;

      this.result.hidden = false;

      if (this.resultIconAvailable) {
        this.resultIconAvailable.hidden = true;
      }

      requestAnimationFrame(() => {
        this.result.classList.add('is-visible');
      });

      if (this.resultSize) {
        this.resultSize.textContent = '';
      }

      if (this.resultText) {
        this.resultText.textContent = message;
      }

      if (this.selectButton) {
        this.selectButton.hidden = true;
        this.selectButton.disabled = true;
      }

      if (this.selectAlternativeButton) {
        this.selectAlternativeButton.hidden = true;
        this.selectAlternativeButton.disabled = true;
      }
    }

    findVariantForSize(sizeLabel) {
      const variants = this.config.product?.variants || [];
      const selectedOptions = this.getSelectedOptions();

      return variants.find((variant) => {
        if (variant.options[this.sizeOptionIndex] !== sizeLabel) return false;

        return variant.options.every((optionValue, index) => {
          if (index === this.sizeOptionIndex) return true;
          if (!selectedOptions[index]) return true;

          return selectedOptions[index] === optionValue;
        });
      });
    }

    getSelectedOptions() {
      const selectedOptions = [];
      const variantPicker =
        document.querySelector(`variant-picker[data-product-id="${this.config.product?.id}"]`) ||
        document.querySelector('variant-picker');

      if (!variantPicker) return selectedOptions;

      variantPicker.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.fieldsetIndex);
        const checkedInput = fieldset.querySelector('input:checked');

        if (checkedInput) {
          selectedOptions[index] = checkedInput.value;
        }
      });

      variantPicker.querySelectorAll('.variant-option__select').forEach((select, index) => {
        if (select.value) {
          selectedOptions[index] = select.value;
        }
      });

      return selectedOptions;
    }

    selectSize(sizeLabel) {
      const variantPicker =
        document.querySelector(`variant-picker[data-product-id="${this.config.product?.id}"]`) ||
        document.querySelector('variant-picker');

      if (!variantPicker) return;

      const radioInputs = variantPicker.querySelectorAll(
        `input[data-fieldset-index="${this.sizeOptionIndex}"]`
      );

      const matchingRadio = Array.from(radioInputs).find((input) => input.value === sizeLabel);

      if (matchingRadio) {
        matchingRadio.click();
        matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
        return;
      }

      const selects = variantPicker.querySelectorAll('.variant-option__select');
      const sizeSelect = selects[this.sizeOptionIndex];

      if (sizeSelect) {
        sizeSelect.value = sizeLabel;
        sizeSelect.dispatchEvent(new Event('input', { bubbles: true }));
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
      }
    }

    parseNumber(value) {
      if (!value) return null;

      const normalizedValue = String(value).trim().replace(',', '.');
      const number = Number.parseFloat(normalizedValue);

      return Number.isFinite(number) ? number : null;
    }
  }

  customElements.define('custom-size-advisor', CustomSizeAdvisor);

  if (!window.customSizeAdvisorTriggerBound) {
    window.customSizeAdvisorTriggerBound = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-custom-size-advisor-open]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      const advisorId = trigger.getAttribute('data-custom-size-advisor-open');
      const advisor = document.getElementById(advisorId);

      if (advisor && typeof advisor.open === 'function') {
        advisor.open();
      }
    });
  }

  if (!window.customDrawerInputModalityBound) {
    window.customDrawerInputModalityBound = true;

    document.documentElement.setAttribute('data-input-modality', 'pointer');

    window.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Tab') {
          document.documentElement.setAttribute('data-input-modality', 'keyboard');
        }
      },
      true
    );

    ['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(
        eventName,
        () => {
          document.documentElement.setAttribute('data-input-modality', 'pointer');
        },
        true
      );
    });
  }
})();